import { useState, useEffect, forwardRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { Button, Modal } from "@mui/material";
import { isEmpty } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import Alert from "@mui/material/Alert";
import Select from "../../common/filters/Select/Select";
import EUPriceListLevelPrices from "./EUPriceListLevelPrices";
import EUCountryLevelPrices from "./EUCountryLevelPrices";
import {
    fetchCountryLevelPrices,
    getPriceFileMaterials,
    setCurrentTab,
    resetPageData,
    setPriceFileId,
    resetCalledOnFileUploadFlag,
    validatePastedAttributes,
    setCurrentPage,
    setSelectedAttributeOptions,
    setSelectedSeparatorOptions,
    setPasteAreaValue,
    setValidValues,
    setInvalidValues,
} from "../../../store/features/workbench/workbench";
import { getMaterialFetchData } from "./euHelper";
import { toastError } from "../../../store/features/global/global";
import {
    resetSavedFilters,
    setSavedFilters,
} from "../../../store/features/filters/filters";
import CustomAccordion from "../../common/customAccordian/CustomAccordion";
import FlatFilter from "../../common/flatFilter/FlatFilter";

import styles from "./EUPricePlanning.module.scss";
import pageStyles from "./EUPricePlanningFilterAttributesMaterials-New.module.scss";
import filterOverlayStyles from "../../common/filters/FilterOverlay.module.scss";
import EUBuyersScreen from "./rollupsComponents/EUBuyersScreen";
import { RegionalPricingTab } from "./EUPricePlanningPlanGroupMaterials";
import { HISTORICAL_DATA } from "../../../constants/Constants";
import WarningIcon from "@mui/icons-material/Warning";

export const page = "filterByAttributes";
const screen = "EU_VIEW_BY_FILTERING_ATTRIBUTES";

export const separatorsMap = {
    space: [" "],
    comma: [","],
    semiColon: [";"],
    tab: ["\t", "\\t"],
    carriageReturn: ["\r", "\\r"],
    newLine: ["\n", "\\n"],
};

export const attributeOptions = [
    { label: "Board Number", value: "board_cd" },
    { label: "GFE Pre-pack", value: "gfe_material_type_ind" },
    { label: "Item Number", value: "item_number" },
    { label: "Legacy Material Number", value: "legacy_material_nbr" },
    { label: "Material Number", value: "material_number" },
    { label: "Style Number", value: "style_number" },
    { label: "Vendor Material Number", value: "vendor_material_nbr" },
];

export const separatorOptions = [
    { label: "Space", value: "space" },
    { label: "Comma", value: "comma" },
    { label: "Semi-colon", value: "semiColon" },
    { label: "Tab", value: "tab" },
    { label: "New line", value: "newLine" },
    { label: "Carriage return", value: "carriageReturn" },
];

export const RegionPricingTabHeader = forwardRef((props, ref) => {
    const {
        general: { countryPriceRequestPendingMaterials },
    } = useSelector((state) => state.workbench);
    const {
        compLoading: {
            basePricing: basePricingLoader,
            regionalPricing: regionalPricingLoader,
        },
    } = useSelector((state) => state.global);

    return (
        <button {...props} forwardedRef={ref}>
            <div className="align-center">
                <span
                    style={
                        countryPriceRequestPendingMaterials.length ||
                        basePricingLoader !== 0 ||
                        regionalPricingLoader !== 0
                            ? { marginRight: 8 }
                            : null
                    }
                >
                    Regional Pricing
                </span>
                {countryPriceRequestPendingMaterials.length ? (
                    <HourglassTopIcon
                        className={`${styles["being-generated"]}`}
                        fontSize="small"
                    />
                ) : null}
                {basePricingLoader !== 0 || regionalPricingLoader !== 0 ? (
                    <RotateRightIcon
                        className={`${styles["being-generated"]}`}
                        fontSize="small"
                    />
                ) : null}
            </div>
        </button>
    );
});

function EUPricePlanningFilterAttributesMaterials(props) {
    const { onReuploadPriceFileClick } = props;

    const dispatch = useDispatch();

    const [filtersExpanded, setFiltersExpanded] = useState(true);
    const [selectedMerchFilters, setSelectedMerchFilters] = useState({});
    const [selectedAttributeFilters, setSelectedAttributeFilters] = useState(
        {}
    );
    const [selectedProductFilters, setSelectedProductFilters] = useState({});
    const [selectedPlanFilters, setSelectedPlanFilters] = useState({});
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [separatedPastedItems, setSeparatedPastedItems] = useState([]);
    const [pasteSubmitDisabled, setPasteSubmitDisabled] = useState(true);
    const [showModal, setShowModal] = useState(false);

    const {
        selectedMarkets,
        selectedRegions,
        selectedChannels,
        priceFileId,
        calledOnFileUploadFlag,
        countryPriceRequestPendingMaterials,
    } = useSelector((state) => state.workbench.general);
    const {
        currentTab,
        materials,
        countryLevelPrices,
        currentPage,
        selectedAttributeOptions,
        selectedSeparatorOptions,
        pasteAreaValue,
        validValues,
        invalidValues,
    } = useSelector((state) => state.workbench[page]);
    const selectedFilters = useSelector(
        (state) => state.filters.savedFilters["EU_VIEW_BY_FILTERING_ATTRIBUTES"]
    );
    const {
        compLoading: {
            basePricing: basePricingLoader,
            regionalPricing: regionalPricingLoader,
        },
    } = useSelector((state) => state.global);

    const regionalPricingTab = useCallback(
        (props) => RegionalPricingTab(props, selectedMarkets),
        [selectedMarkets]
    );

    useEffect(() => {
        if (materials.length || countryLevelPrices.length) {
            dispatch(setCurrentPage({ page, data: 1 }));

            setFiltersExpanded(false);
        }

        if (!basePricingLoader) {
            dispatch(
                resetSavedFilters({ screen: "EU_VIEW_BY_FILTERING_ATTRIBUTES" })
            );
        }

        dispatch(setSelectedAttributeOptions([attributeOptions[4]]));

        return () => {
            dispatch(resetPageData({ page: "filterByAttributes" }));

            dispatch(setPriceFileId(null));
        };
    }, []);

    useEffect(() => {
        const {
            SELECT_FILTERS_MERCH: selectedMerchFilters,
            SELECT_FILTERS_ATTRIBUTE: selectedAttributeFilters,
            SELECT_FILTERS_PRODUCT: selectedProductFilters,
            SELECT_FILTERS_PLANNING: selectedPlanFilters,
        } = selectedFilters;

        setSelectedMerchFilters(selectedMerchFilters);
        setSelectedAttributeFilters(selectedAttributeFilters);
        setSelectedProductFilters(selectedProductFilters);
        setSelectedPlanFilters(selectedPlanFilters);
    }, [selectedFilters]);

    useEffect(() => {
        if (calledOnFileUploadFlag) {
            dispatch(resetCalledOnFileUploadFlag());

            dispatch(setCurrentPage({ page, data: 1 }));

            setFiltersExpanded(false);
        }
    }, [calledOnFileUploadFlag, dispatch]);

    useEffect(() => {
        const separators = [];
        let disabled = false;
        let separatedItems = [
            pasteAreaValue
                .split("")
                .filter((char) => char !== "'")
                .join(""),
        ];

        for (const { value } of selectedSeparatorOptions) {
            separators.push.apply(separators, separatorsMap[value]);
        }

        for (const separator of separators) {
            const temp = [];

            for (const item of separatedItems) {
                temp.push.apply(
                    temp,
                    item
                        .split(separator)
                        .map((str) => str.trim())
                        .filter((str) => str)
                );
            }

            separatedItems = temp;
        }

        if (
            !selectedAttributeOptions.length ||
            !selectedSeparatorOptions.length ||
            !pasteAreaValue ||
            !separatedItems?.length
        ) {
            disabled = true;
        }

        setSeparatedPastedItems(separatedItems);
        setPasteSubmitDisabled(disabled);
    }, [selectedAttributeOptions, selectedSeparatorOptions, pasteAreaValue]);

    const handleAccordion = () => {
        setFiltersExpanded((prevValue) => !prevValue);
    };

    const onMerchFilterSelect = useCallback((data) => {
        setSelectedMerchFilters(data);
    }, []);

    const onAttributeFilterSelect = useCallback((data) => {
        setSelectedAttributeFilters(data);
    }, []);

    const onProductFilterSelect = useCallback((data) => {
        setSelectedProductFilters(data);
    }, []);

    const onPlanFilterSelect = useCallback((data) => {
        setSelectedPlanFilters(data);
    }, []);

    const getMaterials = (submittedFilters) => {
        if (
            selectedMarkets.length &&
            selectedRegions.length &&
            selectedChannels.length
        ) {
            const data = getMaterialFetchData(
                submittedFilters,
                selectedMarkets,
                selectedRegions,
                selectedChannels,
                priceFileId
            );

            dispatch(getPriceFileMaterials({ page, data })).then((res) => {
                if (!res?.table_data.length) {
                    dispatch(
                        toastError(
                            "No material tagged for the selection you have made"
                        )
                    );
                }
                const isHistoricalData = HISTORICAL_DATA.includes(
                    selectedMarkets[0].label
                );
                if (!isHistoricalData) {
                    dispatch(
                        fetchCountryLevelPrices({
                            page,
                            data: {
                                ...data,
                                price_file_material_ids: res?.table_data.map(
                                    (e) => e.id
                                ),
                            },
                        })
                    );
                }
            });
        }
    };

    // const selectedAttribute = selectedAttributeOptions[0].value;
    // const submittedFilters = {
    //     SELECT_FILTERS_ATTRIBUTE: {
    //         [selectedAttribute]: validValues,
    //     },
    // };

    const onFilterSubmitClick = async (clickedFromModal) => {
        setShowModal(false);
        let submittedFilters = {};
        const selectedAttribute = selectedAttributeOptions[0]?.value;

        if (pasteAreaValue) {
            const data = await callValidatePastedAttributes();
            const { valid_values, invalid_values } = data;

            if (invalid_values.length && !clickedFromModal) {
                setShowModal(true);
                return;
            }

            submittedFilters.SELECT_FILTERS_ATTRIBUTE = {
                [selectedAttribute]: valid_values,
            };
        }

        if (!isEmpty(selectedMerchFilters)) {
            submittedFilters.SELECT_FILTERS_MERCH = selectedMerchFilters;
        }

        if (!isEmpty(selectedAttributeFilters)) {
            submittedFilters.SELECT_FILTERS_ATTRIBUTE = {
                ...(submittedFilters?.SELECT_FILTERS_ATTRIBUTE || {}),
                ...selectedAttributeFilters,
            };
        }

        if (!isEmpty(selectedProductFilters)) {
            submittedFilters.SELECT_FILTERS_PRODUCT = selectedProductFilters;
        }

        if (!isEmpty(selectedPlanFilters)) {
            submittedFilters.SELECT_FILTERS_PLANNING = selectedPlanFilters;
        }

        if (isEmpty(submittedFilters)) {
            dispatch(toastError("Please select some filters to continue"));

            return;
        }

        dispatch(setSavedFilters({ screen, data: submittedFilters }));

        dispatch(setCurrentPage({ page, data: 1 }));
        setFiltersExpanded(false);

        getMaterials(submittedFilters);
    };

    const onFilterClearClick = () => {
        setSelectedMerchFilters({});
        setSelectedAttributeFilters({});
        setSelectedProductFilters({});
        setSelectedPlanFilters({});
        dispatch(setPasteAreaValue(""));
    };

    const onPasteClick = () => {
        setDrawerOpen(true);
    };

    const onAddMoreProductsClick = () => {
        setDrawerOpen(true);
    };

    const onTabClick = (_, newTab) => {
        dispatch(setCurrentTab({ page, data: newTab }));
    };

    const navigateToCountryLevelPrices = () => {
        onTabClick(null, "country");
    };

    const onDrawerClose = () => {
        setDrawerOpen(false);
        dispatch(setValidValues([]));
        dispatch(setInvalidValues([]));
    };

    const onProceedWithValidValuesClick = () => {
        const selectedAttribute = selectedAttributeOptions[0].value;
        const filters = {
            SELECT_FILTERS_ATTRIBUTE: {
                [selectedAttribute]: validValues,
            },
        };

        dispatch(setSavedFilters({ screen, data: filters }));

        getMaterials(filters);
        dispatch(setCurrentPage({ page, data: 2 }));
    };

    const onBackToPasteClick = () => {
        setShowModal(false);
        dispatch(setValidValues([]));
        dispatch(setInvalidValues([]));
    };

    const callValidatePastedAttributes = () => {
        return new Promise((resolve) => {
            const selectedAttribute = selectedAttributeOptions[0].value;
            const filters = {
                [selectedAttribute]: separatedPastedItems,
            };

            if (
                selectedMarkets.length &&
                selectedRegions.length &&
                selectedChannels.length
            ) {
                const data = {
                    market_id: selectedMarkets[0].value,
                    region: selectedRegions[0].label,
                    channel: selectedChannels[0].label,
                    filters,
                };

                dispatch(validatePastedAttributes(data)).then((data) => {
                    const { valid_values, invalid_values } = data;

                    dispatch(setValidValues(valid_values));
                    dispatch(setInvalidValues(invalid_values));
                    resolve(data);
                });
            }
        });
    };

    const onSelectedAttributeChange = (data) => {
        dispatch(setSelectedAttributeOptions(data.selectedItems));
    };

    const onSelectedSeparatorChange = (data) => {
        dispatch(setSelectedSeparatorOptions(data.selectedItems));
    };

    const onPasteAreaValueChange = (e) => {
        dispatch(setPasteAreaValue(e.target.value));
    };

    const getInvalidaMaterialsText = () => {
        if (validValues.length) {
            return (
                <>
                    The uploaded list contains{" "}
                    <span className="font-bold">{invalidValues.length} item(s)</span>{" "}that&nbsp;
                </>
            );
        }

        return "All the items pasted";
    };

    return (
        <div className={`${pageStyles["body"]}`}>
            {currentPage === 0 || currentPage === 1 ? (
                <div className={`${pageStyles["filter-container"]}`}>
                    <CustomAccordion
                        label="Select Filters"
                        expanded={filtersExpanded}
                        handleAccordion={handleAccordion}
                        childProps={{
                            className: "expanded-accordion",
                            style: { padding: "0px 0px 10px" },
                        }}
                    >
                        <div
                            style={{
                                border: "1px solid #C8CED7",
                                borderRadius: "3px",
                            }}
                        >
                            <CustomAccordion
                                label="Merch Division"
                                defaultExpanded={false}
                            >
                                <FlatFilter
                                    screenName="SELECT_FILTERS_MERCH_DIVISION"
                                    onFilterSelect={onMerchFilterSelect}
                                    selectedFilters={selectedMerchFilters}
                                />
                            </CustomAccordion>
                            <div
                                className={`${filterOverlayStyles.divider}`}
                            ></div>
                            <CustomAccordion
                                label="Attributes"
                                defaultExpanded={false}
                            >
                                <FlatFilter
                                    screenName="SELECT_FILTERS_ATTRIBUTES"
                                    onFilterSelect={onAttributeFilterSelect}
                                    selectedFilters={selectedAttributeFilters}
                                />
                            </CustomAccordion>
                            <div
                                className={`${filterOverlayStyles.divider}`}
                            ></div>
                            <CustomAccordion
                                label="Merch Hierarchy"
                                defaultExpanded={false}
                            >
                                <FlatFilter
                                    screenName="SELECT_FILTERS_MERCH"
                                    onFilterSelect={onMerchFilterSelect}
                                    selectedFilters={selectedMerchFilters}
                                />
                            </CustomAccordion>
                            <div
                                className={`${filterOverlayStyles.divider}`}
                            ></div>
                            <CustomAccordion
                                label="Product Hierarchy"
                                defaultExpanded={false}
                            >
                                <FlatFilter
                                    screenName="SELECT_FILTERS_PRODUCT"
                                    onFilterSelect={onProductFilterSelect}
                                    selectedFilters={selectedProductFilters}
                                />
                            </CustomAccordion>
                            <div
                                className={`${filterOverlayStyles.divider}`}
                            ></div>
                            <CustomAccordion
                                label="Planning Hierarchy"
                                defaultExpanded={false}
                            >
                                <FlatFilter
                                    screenName="SELECT_FILTERS_PLANNING"
                                    onFilterSelect={onPlanFilterSelect}
                                    selectedFilters={selectedPlanFilters}
                                />
                            </CustomAccordion>
                            <div
                                className={`${filterOverlayStyles.divider}`}
                            ></div>
                            <CustomAccordion
                                label="Brand Group"
                                defaultExpanded={false}
                            >
                                <FlatFilter
                                    screenName="SELECT_FILTERS_BRAND_GROUP"
                                    onFilterSelect={onMerchFilterSelect}
                                    selectedFilters={selectedMerchFilters}
                                />
                            </CustomAccordion>
                            <div
                                className={`${filterOverlayStyles.divider}`}
                            ></div>
                            <CustomAccordion
                                label="Select and Paste Attributes"
                                defaultExpanded={false}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "flex-start",
                                        textAlign: "left",
                                    }}
                                >
                                    <div style={{ width: "305px" }}>
                                        <label
                                            className={`${pageStyles["sub-title"]} select-label mb-4`}
                                        >
                                            Select attribute{" "}
                                            <span style={{ color: "red" }}>
                                                *
                                            </span>
                                        </label>
                                        <Select
                                            className={`${pageStyles["dropdown"]}`}
                                            initialData={attributeOptions}
                                            selectedOptions={
                                                selectedAttributeOptions
                                            }
                                            updateSelected={
                                                onSelectedAttributeChange
                                            }
                                            hideClearSelection
                                            updateSelectedOnEachSelection
                                        />
                                    </div>

                                    <div style={{ width: "305px" }}>
                                        <label
                                            className={`${pageStyles["sub-title"]} select-label`}
                                        >
                                            Select separators{" "}
                                            <span style={{ color: "red" }}>
                                                *
                                            </span>
                                        </label>
                                        <Select
                                            className={`${pageStyles["dropdown"]}`}
                                            initialData={separatorOptions}
                                            selectedOptions={
                                                selectedSeparatorOptions
                                            }
                                            updateSelected={
                                                onSelectedSeparatorChange
                                            }
                                            updateSelectedOnEachSelection
                                            is_multiple_selection
                                        />
                                    </div>
                                </div>
                                <div className="textarea-container">
                                    <label
                                        className={`${pageStyles["sub-title"]} select-label`}
                                    >
                                        Paste values
                                    </label>
                                    <textarea
                                        className={`${pageStyles["paste-area"]}`}
                                        placeholder="Kindly paste the values here and click on 'Submit' to see results"
                                        value={pasteAreaValue}
                                        onChange={onPasteAreaValueChange}
                                    ></textarea>
                                </div>
                                {/* <div
                                    className={`${pageStyles["paste-alert"]}`}
                                    style={{
                                        width: "100%",
                                        marginTop: "8px",
                                        textAlign: "left",
                                    }}
                                >
                                    {invalidValues.length ? (
                                        <>
                                            <Alert
                                                variant="filled"
                                                severity="warning"
                                                style={{
                                                    marginBottom: "8px",
                                                }}
                                            >
                                                <p
                                                    style={{
                                                        font: "normal normal normal 14px/26px Poppins, sans-serif",
                                                    }}
                                                >
                                                    {validValues.length
                                                        ? `The uploaded list contains ${invalidValues.length} item(s) that`
                                                        : `All the items pasted`}{" "}
                                                    don't belong in the selected
                                                    region-market-channel
                                                    combination
                                                </p>
                                                <p>
                                                    {invalidValues.join(", ")}
                                                </p>
                                            </Alert>
                                            <div className="align-center button-group-flex">
                                                {validValues.length ? (
                                                    <Button
                                                        variant="contained"
                                                        onClick={
                                                            onProceedWithValidValuesClick
                                                        }
                                                    >
                                                        Proceed with valid items
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    variant="outlined"
                                                    onClick={onBackToPasteClick}
                                                >
                                                    Back
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            disabled={pasteSubmitDisabled}
                                            onClick={callValidatePastedAttributes}
                                        >
                                            Validate
                                        </Button>
                                    )}
                                </div> */}
                            </CustomAccordion>
                            <div
                                className={`${filterOverlayStyles.divider}`}
                            ></div>
                            <div
                                className={`${filterOverlayStyles["action-container"]}`}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => onFilterSubmitClick(false)}
                                >
                                    Submit
                                </Button>
                                <Button
                                    className={`${filterOverlayStyles["tertiary-button"]}`}
                                    onClick={onFilterClearClick}
                                >
                                    Clear Filter
                                </Button>
                            </div>
                        </div>
                    </CustomAccordion>
                </div>
            ) : null}
            {currentPage === 2 ? (
                <div className="center-space-between mt-20">
                    <div style={{ fontWeight: "600" }}>EU Price Planning</div>
                    <Button
                        variant="contained"
                        onClick={onAddMoreProductsClick}
                    >
                        Edit Attribute Selection
                    </Button>
                </div>
            ) : null}
            {currentPage > 0 ? (
                <div className={`${styles["view-by-filtering-attributes"]}`}>
                    <div className={`${styles["level-price-container"]}`}>
                        <TabContext value={currentTab}>
                            <TabList onChange={onTabClick}>
                                <Tab label="Base Pricing" value="price-list" />
                                <Tab
                                    label="Regional Pricing"
                                    value="country"
                                    component={regionalPricingTab}
                                    disabled={
                                        countryPriceRequestPendingMaterials.length ||
                                        basePricingLoader !== 0 ||
                                        regionalPricingLoader !== 0
                                    }
                                />
                                <Tab label="Buying Rollups" value="buyer" />
                            </TabList>
                            <TabPanel
                                value="price-list"
                                currentTab={currentTab}
                            >
                                <EUPriceListLevelPrices
                                    page={page}
                                    filterPage="EU_VIEW_BY_FILTERING_ATTRIBUTES"
                                    navigateToCountryLevelPrices={
                                        navigateToCountryLevelPrices
                                    }
                                    onReuploadPriceFileClick={
                                        onReuploadPriceFileClick
                                    }
                                    currentTab={currentTab}
                                />
                            </TabPanel>
                            <TabPanel value="country" currentTab={currentTab}>
                                <EUCountryLevelPrices
                                    page={page}
                                    filterPage="EU_VIEW_BY_FILTERING_ATTRIBUTES"
                                    currentTab={currentTab}
                                />
                            </TabPanel>
                            <TabPanel value="buyer" currentTab={currentTab}>
                                <EUBuyersScreen
                                    page={page}
                                    filterPage="EU_VIEW_BY_FILTERING_ATTRIBUTES"
                                    currentTab={currentTab}
                                />
                            </TabPanel>
                        </TabContext>
                    </div>
                </div>
            ) : null}
            <Modal
                open={showModal}
                aria-labelledby="copy-paste-confirmation-modal"
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <div className="copy-paste-confirmation-modal flex-column">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <div className="modal-title">
                            Paste Attributes Confimation Prompt
                        </div>
                        <div
                            className="cross-icon"
                            onClick={() => setShowModal(false)}
                        >
                            &#10005;
                        </div>
                    </div>

                    <div className="copy-paste-confirmation-modal-content">
                        <div className="warning_alert">
                            <p>
                                <WarningIcon />
                                {getInvalidaMaterialsText()}
                                don't belong in the selected
                                region-market-channel combination
                            </p>
                        </div>
                        <div className="alert_attributes_container">
                            {invalidValues.map((item) => (
                                <span>{item}</span>
                            ))}
                        </div>
                    </div>
                    <div
                        className={`${styles["modal-buttons"]} button-group-flex`}
                    >
                        <Button variant="text" onClick={onBackToPasteClick}>
                            Back
                        </Button>
                        {validValues?.length > 0 && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    onFilterSubmitClick(true);
                                }}
                            >
                                Proceed With Valid Material
                            </Button>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default EUPricePlanningFilterAttributesMaterials;

export function TabPanel(props) {
    const { children, className, style, value, currentTab, ...other } = props;

    return (
        <div
            className={className}
            style={{
                width: "100%",
                ...style,
                marginTop: "24px",
                display: currentTab !== value ? "none" : undefined,
            }}
            {...other}
        >
            {children}
        </div>
    );
}
